import { Suspense, lazy, useEffect, useRef, useState, type ComponentType } from "react";

type Props<P extends object> = {
  /** Dynamisk import av komponenten. Måste vara en stabil funktion (definiera utanför render). */
  load: () => Promise<{ default: ComponentType<P> }>;
  /** Props som skickas till den laddade komponenten. */
  componentProps?: P;
  /** Reserverad höjd så att inget hoppar (CLS). */
  minHeight?: number;
  /** Hur långt före viewporten vi börjar ladda. */
  rootMargin?: string;
  /** Enkel platshållare medan chunken laddas. */
  placeholder?: React.ReactNode;
  label?: string;
};

/**
 * "Island"-hydrering: tung, icke-SEO-kritisk interaktivitet laddas som en egen
 * chunk först när den närmar sig viewporten. Servern renderar bara en
 * platshållare med reserverad höjd, så initial JS och INP blir betydligt bättre.
 */
export function DeferredIsland<P extends object>({
  load,
  componentProps,
  minHeight = 200,
  rootMargin = "300px",
  placeholder,
  label,
}: Props<P>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [Component, setComponent] = useState<ComponentType<P> | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  useEffect(() => {
    if (!visible || Component) return;
    let cancelled = false;
    void load().then((mod) => {
      if (!cancelled) setComponent(() => lazy(() => Promise.resolve(mod)) as unknown as ComponentType<P>);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, Component, load]);

  return (
    <div ref={ref} style={{ minHeight }} aria-busy={visible && !Component ? true : undefined}>
      {Component ? (
        <Suspense fallback={placeholder ?? <IslandSkeleton minHeight={minHeight} label={label} />}>
          <Component {...(componentProps as P)} />
        </Suspense>
      ) : (
        (placeholder ?? <IslandSkeleton minHeight={minHeight} label={label} />)
      )}
    </div>
  );
}

function IslandSkeleton({ minHeight, label }: { minHeight: number; label?: string }) {
  return (
    <div
      className="flex items-center justify-center rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground"
      style={{ minHeight }}
    >
      {label ?? "Laddar verktyg…"}
    </div>
  );
}
